// ChatContext.tsx
import { createContext, useContext, useState, useEffect, useRef } from 'react';
import { EventSourcePolyfill } from '../chat/chatPanel/util';
import { ProcessDefinitionList } from '@/api/chat';

export type ThemeMode = 'light' | 'dark';
export type AssistantPanelType = 'general' | 'navigation';

export interface AssistantOpenRequest {
  type: AssistantPanelType;
  params?: Record<string, unknown>;
}

export interface ConversationItem {
  id: string;
  name: string;
  type: string;
  agentId?: number;      // 智能体 ID
  agentName?: string;    // 智能体名称
}

export interface ChatContextProps {
  config?: Record<string, any>;
  ready?: boolean;
  setReady?: React.Dispatch<React.SetStateAction<boolean>>;
  isTalking?: boolean;
  setTalking?: React.Dispatch<React.SetStateAction<boolean>>;
  conversation?: ConversationItem;
  setConversation?: React.Dispatch<React.SetStateAction<ConversationItem | undefined>>;
  history?: ConversationItem;
  setHistory?: React.Dispatch<React.SetStateAction<ConversationItem | undefined>>;
  processInfo?: ProcessDefinitionList;
  setProcessInfo?: React.Dispatch<React.SetStateAction<ProcessDefinitionList | undefined>>;
  openRequest?: AssistantOpenRequest;
  setOpenRequest?: React.Dispatch<React.SetStateAction<AssistantOpenRequest | undefined>>;
  theme?: ThemeMode;
}

const ChatContext = createContext<ChatContextProps>({});

export const ChatProvider = ({
  children,
  panelType = 'general',
}: {
  children: React.ReactNode;
  panelType?: AssistantPanelType;
}) => {
  const [ready, setReady] = useState(false);
  const [isTalking, setTalking] = useState<boolean>(false);
  const [processInfo, setProcessInfo] = useState<ProcessDefinitionList>();
  const [conversation, setConversation] = useState<ConversationItem>();
  const [history, setHistory] = useState<ConversationItem>();
  const [config, setConfig] = useState<Record<string, any>>({});
  const [openRequest, setOpenRequest] = useState<AssistantOpenRequest>();
  const [theme, setTheme] = useState<ThemeMode>('dark');
  const readyRef = useRef(false);

  const applyThemeToDOM = (themeMode: ThemeMode) => {
    document.documentElement.setAttribute('data-theme', themeMode);
  };

  useEffect(() => {
    const applyConfig = (nextConfig: Record<string, any> | undefined) => {
      if (!nextConfig) return;
      window.assistantConfig = nextConfig;
      setConfig(nextConfig);
      const configTheme = nextConfig.theme === 'light' ? 'light' : 'dark';
      setTheme(configTheme);
      applyThemeToDOM(configTheme);
      readyRef.current = true;
      setReady(true);
      try {
        if (window.parent && window.parent !== window) {
          window.parent.postMessage({ type: 'assistant-config-received' }, '*');
        }
      } catch {
        // ignore postMessage errors
      }
    };

    const handleMessage = (event: MessageEvent) => {
      if (event.data?.type === 'initConfig') {
        applyConfig(event.data.payload);
      }
      if (event.source === window.parent && event.data?.type === 'assistant-open-request') {
        const request = event.data.payload;
        if (request?.type === panelType) {
          setOpenRequest({
            type: panelType,
            params:
              request.params && typeof request.params === 'object' && !Array.isArray(request.params)
                ? request.params
                : undefined,
          });
        }
      }
    };

    const requestConfig = () => {
      try {
        if (window.parent && window.parent !== window) {
          window.parent.postMessage({ type: 'assistant-config-request' }, '*');
        }
      } catch {
        // ignore postMessage errors
      }
    };

    window.addEventListener('message', handleMessage);

    if (window.assistantConfig) {
      applyConfig(window.assistantConfig);
    }

    requestConfig();
    const retryTimer = window.setInterval(() => {
      if (!readyRef.current) {
        requestConfig();
      }
    }, 1000);

    return () => {
      window.removeEventListener('message', handleMessage);
      window.clearInterval(retryTimer);
    };
  }, [panelType]);

  return (
    <ChatContext.Provider
      value={{
        ready,
        config,
        history,
        isTalking,
        processInfo,
        openRequest,
        conversation,
        theme,
        setReady,
        setTalking,
        setHistory,
        setProcessInfo,
        setOpenRequest,
        setConversation,
      }}
    >
      {children}
    </ChatContext.Provider>
  );
};

export const useChatContext = () => useContext(ChatContext);
